<template>
  <div class="h-100 d-flex flex-column justify-space-between">
    <EmptyState
      v-if="!finding"
      style="height: 100vh"
      :title="$t('assignments.address_empty_state_title')"
      :message="$t('assignments.address_empty_state_message')"
    />
    <div
      v-else-if="finding"
      class="h-100 d-flex flex-column justify-space-between"
    >
      <div>
        <div class="d-flex justify-space-between align-center px-6 py-4">
          <p class="body-1 font-weight-medium pa-0 ma-0">
            {{ $t('assignments.address_title') }}
          </p>
        </div>

        <v-divider />
        <p class="text-heading-6 font-bold px-6 mt-4">
          {{ situation }}
        </p>

        <div class="px-6 py-0 mb-4">
          <router-link
            v-if="executionName"
            class="d-flex cursor-pointer text-decoration-none text-caption ma-0 pa-0"
            :to="{
              name: 'routineExecutionDetail',
              params: { routineId: routine.id },
            }"
          >
            <v-icon small left color="primary"
              >mdi-clipboard-check-outline</v-icon
            >
            {{ $t('routines.execution_name', { name: executionName }) }}
          </router-link>
        </div>

        <div v-if="showSolution">
          <v-textarea
            v-model="solution"
            class="mt-4 px-6"
            data-testid="findingAddressForm-input-solution"
            dense
            :disabled="isSubmitting"
            hide-details
            auto-grow
            outlined
            rows="1"
            row-height="15"
            :label="$t('assignments.solution_input_label')"
            :maxlength="$constants.findings.solutionMaxLength"
          />

          <div class="px-6 mt-4">
            <p class="grey--text text--darken-1 font-size-12">
              {{ $t('assignments.solution_evidence_label') }}
            </p>
            <MediaContainer
              onlyThumbnails
              :images="solutionEvidenceMedia"
              :readOnly="isSubmitting"
              @change="handleMediaChange"
            />
          </div>
        </div>
      </div>

      <div class="my-6 action-buttons">
        <v-divider v-if="showActions" class="my-4" />

        <v-row class="ma-0 pa-0 col-12">
          <v-col>
            <Button
              class="w-100"
              data-testid="findingAddressForm-button-back"
              outlined
              :text="$t('common.button.back')"
              @click="$emit('goBack')"
            />
          </v-col>

          <v-col v-if="canCreateQuickAction">
            <Button
              class="w-100"
              color="primary"
              data-testid="findingAddressForm-button-createQuickAction"
              :disabled="!canSubmitQuickAction"
              :loading="isSubmitting"
              :text="createQuickActionButtonLabel"
              @click="handleCreateQuickAction"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { utils } from 'frontend-js-shared'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'

import revMixin from '@/mixins/revMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import findingPermissions from '@/mixins/findingPermissions.vue'

import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import EmptyState from '@/components/EmptyState.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'FindingAddressDrawer',

  mixins: [mixin, revMixin, trackerMixin, findingPermissions],

  components: {
    EmptyState,
    MediaContainer,
    Button,
  },

  props: {
    finding: {
      type: Object,
      required: true,
    },
    addressMode: {
      type: String,
      default: null,
    },
    routine: {
      type: Object,
      required: true,
    },
    routineDefinition: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      improvementIdeaPascalCase: this.pascalCase(
        this.$constants.revTypes.improvementIdea
      ),
      quickActionPascalCase: this.pascalCase(
        this.$constants.revTypes.quickAction
      ),
      isSubmitting: false,
      solution: '',
      solutionEvidenceMedia: [],
    }
  },

  computed: {
    ...mapGetters(['account', 'organization']),

    isCompleted() {
      return this.finding?.status === 'completed'
    },

    isQuickActionAddressMode() {
      return this.addressMode === this.$constants.revTypes.quickAction
    },

    canSubmitQuickAction() {
      return Boolean(this.solution?.trim?.())
    },

    permissions() {
      return this.getPermissions({
        routine: this.routine,
        routineDefinition: this.routineDefinition,
        finding: this.finding,
      })
    },

    executionName() {
      return this.routine?.name ?? ''
    },

    canCreateQuickAction() {
      return (
        this.permissions.address &&
        !this.finding?.revId &&
        (this.isCompleted || this.isQuickActionAddressMode) &&
        this.organization?.featuresEnabled__quickAction
      )
    },

    showActions() {
      return this.canCreateQuickAction
    },

    situation() {
      return this.finding?.situation ?? ''
    },

    showSolution() {
      return this.canCreateQuickAction
    },

    quickActionSingularLabel() {
      return (
        this.organization?.customNamings?.quickAction?.singular ||
        this.$t('common.rev_type.quick_action_one')
      )
    },

    createQuickActionButtonLabel() {
      if (this.isQuickActionAddressMode && !this.isCompleted) {
        return this.$t('assignments.finding_resolve_button')
      }

      return this.$t('assignments.finding_create_button', {
        revType: this.quickActionSingularLabel,
      })
    },
  },

  methods: {
    ...mapActions({
      updateQuickAction: 'quickAction/update',
    }),

    handleMediaChange(media) {
      this.solutionEvidenceMedia = media
    },

    onFindingCompleted({ revType, revId }) {
      this.navigateTo({
        name: _.camelCase(revType),
        params: {
          revId,
          organizationId: this.organizationId,
          isFromFinding: true,
        },
      })
    },

    getAddressTrackingPayload(revType) {
      return {
        situation: this.finding?.situation ?? '',
        revType,
        hasMedia: Boolean(this.finding?.media?.length),
        hasAssignees: Boolean(this.finding?.assignees?.length),
        numberOfAssignees: this.finding?.assignees?.length ?? 0,
        hasDueDate: Boolean(this.finding?.dueDate),
      }
    },

    async handleCreateQuickAction() {
      if (this.isSubmitting || !this.canSubmitQuickAction) return

      try {
        this.isSubmitting = true
        const quickAction = await this.createInitialQuickAction()

        await this.completeFinding()
        await this.linkFindingWithRev(
          quickAction?.id,
          this.quickActionPascalCase
        )
        await this.markQuickActionAsCompleted(quickAction)

        this.trackAddressFinding(
          this.getAddressTrackingPayload(this.$constants.revTypes.quickAction)
        )
        this.trackFindingStatusChanged(this.finding, 'button', 'completed', null)
        await this.onFindingCompleted({
          revType: this.quickActionPascalCase,
          revId: quickAction?.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isSubmitting = false
      }
    },

    async createInitialQuickAction() {
      const response = await api.quickaction.create(
        this.finding.organization,
        this.getInitialQuickActionPayload()
      )

      return response.data
    },

    linkFindingWithRev(revId, revType) {
      return api.findings.update({
        organizationId: this.finding.organization,
        findingId: this.finding?.id,
        data: {
          revId,
          revType,
        },
      })
    },

    completeFinding() {
      return api.findings.update({
        organizationId: this.finding.organization,
        findingId: this.finding?.id,
        data: {
          status: 'completed',
        },
      })
    },

    getInitialQuickActionPayload() {
      const collaborators = this.finding.assignees.reduce((result, account) => {
        if (
          account._id !== this.account._id &&
          account._id !== this.finding.createdBy
        ) {
          result.push(account._id)
        }

        return result
      }, [])

      return {
        author: this.account?.id,
        name:
          this.finding?.situation?.substring?.(
            0,
            this.$constants.quickAction.titleMaxLength
          ) ?? '',
        location: {
          organization: this.finding?.organization,
          site: this.finding?.site,
          tags: this.finding?.tags?.map(tag => tag?._id ?? tag?.id ?? tag),
        },
        team: {
          owner: this.account?._id,
          collaborators,
        },
        dueDate: this.finding?.dueDate ?? null,
        situationTypes: this.finding?.situationTypes ?? [],
        findingId: utils.getEntityId(this.finding),
        routineId: utils.getEntityId(this.routine),
      }
    },

    markQuickActionAsCompleted(quickAction) {
      return this.updateQuickAction({
        organizationId: this.finding.organization,
        revId: quickAction?.id,
        data: {
          ...quickAction,
          categories: this.finding.categories ?? [],
          beforePhase: {
            situation: this.finding?.situation,
            media: this.finding?.media ?? [],
            situationTypes: this.finding?.situationTypes ?? [],
          },
          afterPhase: {
            solution: this.solution,
            media: this.solutionEvidenceMedia ?? [],
          },
        },
      })
    },

  },
}
</script>
<style scoped>
.action-buttons {
  width: 100%;
  position: absolute;
  bottom: 0;
}
</style>
